import { BarDatum } from '@nivo/bar'

import type { OriginalDataItem } from './types'

const isRecordWithProperty = (obj: unknown, prop: string): obj is Record<string, unknown> => {
  return typeof obj === 'object' && obj !== null && prop in obj
}

export const isBottomBar = (bar: BarDatum, analysis: Record<string, unknown>): boolean => {
  if (!analysis || !bar.key) return false

  const [segment, category] = String(bar.key).split('.')
  if (!category) return false

  const categoryData = analysis[category]

  if (!isRecordWithProperty(categoryData, 'lastSegment')) return false

  return categoryData.lastSegment === segment
}

export const isTopBar = (bar: BarDatum, analysis: Record<string, unknown>): boolean => {
  if (!analysis || !bar.key) return false

  const [segment, category] = String(bar.key).split('.')
  if (!category) return false

  const categoryData = analysis[category]

  if (!isRecordWithProperty(categoryData, 'firstSegment')) return false

  return categoryData.firstSegment === segment
}

export const createRoundedRectPath = (
  width: number,
  height: number,
  radius: number,
  roundTop = true,
  roundBottom = true
): string => {
  if (height === 0) {
    return ''
  }
  const t = roundTop ? radius : 0
  const b = roundBottom ? radius : 0

  return `
    M ${t} 0 L ${width - t} 0 ${t ? `Q ${width} 0 ${width} ${t}` : ''}
    L ${width} ${height - b} ${b ? `Q ${width} ${height} ${width - b} ${height}` : ''}
    L ${b} ${height} ${b ? `Q 0 ${height} 0 ${height - b}` : ''}
    L 0 ${t} ${t ? `Q 0 0 ${t} 0` : ''} Z
  `
    .replace(/\s+/g, ' ')
    .trim()
}

export const extractStackId = (
  bar: BarDatum,
  data: Record<string, unknown>,
  groupMode: 'grouped' | 'stacked'
): string => {
  const isStacked = groupMode === 'stacked'

  if (isStacked) {
    let stackId = data.bucket || data.score

    if (!stackId && bar.key) {
      const keyStr = String(bar.key)
      const parts = keyStr.split('.')
      stackId = parts.length > 1 ? parts[1] : parts[0]
    }

    return String(stackId || 'unknown')
  }

  if (bar.key) {
    const keyStr = String(bar.key)
    const parts = keyStr.split('.')
    if (parts.length > 1) {
      return parts[1] || 'unknown'
    }
  }

  return String(data.bucket ?? bar.key ?? 'unknown')
}

const hasGetMethod = (obj: unknown): obj is { get: () => string } => {
  return typeof obj === 'object' && obj !== null && 'get' in obj
}

export const getGroupedModeColor = (groupMode: 'grouped' | 'stacked', color: unknown): string => {
  if (hasGetMethod(color)) {
    return color.get()
  }
  return String(color)
}

export const calculateFillOpacity = (
  data: Record<string, unknown>,
  originalData: OriginalDataItem[],
  bar: BarDatum,
  stackId: string,
  isAnySelected: boolean,
  isActive: boolean
): number => {
  // Check for explicit opacity in data
  if (data && '__opacity' in data && typeof data.__opacity === 'number') {
    return data.__opacity
  }

  // Check for opacity in original data by index
  if (
    typeof bar.index === 'number' &&
    originalData?.[bar.index] &&
    '__opacity' in (originalData?.[bar.index] || {})
  ) {
    const originalItem = originalData[bar.index]
    return typeof originalItem?.__opacity === 'number' ? originalItem?.__opacity : 1
  }

  // Find matching item by bucket name
  const foundItem = originalData.find((item) => {
    if (!item || typeof item !== 'object') return false
    const itemBucket = item.bucket || item.name || item.label
    return itemBucket && String(itemBucket).toLowerCase() === stackId.toLowerCase()
  })

  if (foundItem && '__opacity' in foundItem) {
    return typeof foundItem.__opacity === 'number' ? foundItem.__opacity : 1
  }

  return isAnySelected && !isActive ? 0.3 : 1
}
